import { createContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { listenToUserTournamentAdmins } from '../utils/tournamentPermissions';
import { listenToUserPermissions } from '../firebase/userPermissionsUtils';
import { listenToUserModerationStatus, isMuteActive } from '../database/userModerationStatus';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isAdminClaim, setIsAdminClaim] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [tournamentAdminList, setTournamentAdminList] = useState([]);
  const [grantedPermissions, setGrantedPermissions] = useState([]);
  const [moderationStatus, setModerationStatus] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult();
          setIsAdminClaim(idTokenResult.claims.admin === true);
        } catch (error) {
          console.error('Error getting token claims:', error);
          setIsAdminClaim(false);
        }
      } else {
        setUserProfile(null);
        setIsAdminClaim(false);
        setIsAdminUser(false);
        setLoading(false);
        setProfileLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    setProfileLoading(true);

    const userProfileRef = doc(db, 'userProfiles', currentUser.uid);
    const unsubscribe = onSnapshot(
      userProfileRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setUserProfile({ id: currentUser.uid, ...docSnap.data() });
        } else {
          setUserProfile(null);
        }
        setLoading(false);
        setProfileLoading(false);
      },
      (error) => {
        console.error('Error listening to user profile:', error);
        setLoading(false);
        setProfileLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser]);

  // Super admin presence in the adminUsers collection
  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(
      doc(db, 'adminUsers', currentUser.uid),
      (docSnapshot) => setIsAdminUser(docSnapshot.exists()),
      (error) => {
        console.error('Error checking admin status:', error);
        setIsAdminUser(false);
      }
    );
    return unsubscribe;
  }, [currentUser]);

  // Granted permissions listener
  useEffect(() => {
    if (!currentUser) {
      setGrantedPermissions([]);
      return undefined;
    }
    return listenToUserPermissions(currentUser.uid, setGrantedPermissions);
  }, [currentUser]);

  // Tournament admin assignments
  useEffect(() => {
    if (!currentUser) {
      setTournamentAdminList([]);
      return;
    }
    return listenToUserTournamentAdmins(currentUser.uid, setTournamentAdminList);
  }, [currentUser]);

  // Per-user moderation status (bans, mutes)
  useEffect(() => {
    if (!currentUser) {
      setModerationStatus(null);
      return undefined;
    }
    return listenToUserModerationStatus(currentUser.uid, setModerationStatus);
  }, [currentUser]);

  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setIsAdminClaim(false);
      setIsAdminUser(false);
      setTournamentAdminList([]);
      setGrantedPermissions([]);
      setModerationStatus(null);
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      return false;
    }
  };

  // Super admin: the one global role. Implicit: every permission.
  const isSuperAdmin = () => {
    return isAdminClaim || isAdminUser || userProfile?.role === 'admin';
  };

  // Tournament admin: scoped to a specific tournament
  const isTournamentAdmin = (tournamentId) => {
    if (!tournamentId) return false;
    return tournamentAdminList.includes(tournamentId);
  };

  // Effective permissions — super admin wildcard, otherwise explicit permission grant.
  const hasPermission = (key) => {
    if (!key) return false;
    if (isSuperAdmin()) return true;
    return grantedPermissions.includes(key);
  };

  const hasAccessToTournament = (tournamentId) => {
    if (!tournamentId) return false;
    return isSuperAdmin() || isTournamentAdmin(tournamentId);
  };

  // Any admin-ish privilege (used by the catch-all requireAdmin gate + nav hints)
  const isAdmin = () => {
    return isSuperAdmin() || tournamentAdminList.length > 0 || grantedPermissions.length > 0;
  };

  const getTournamentAdminList = () => tournamentAdminList;

  const muteActive = isMuteActive(moderationStatus);
  const isCommentBanned = !!moderationStatus?.commentBan || muteActive;
  const isSiteBanned = !!moderationStatus?.siteBan;
  const muteActiveUntil = muteActive ? moderationStatus.muteUntil : null;

  const value = {
    currentUser,
    userProfile,
    logout,
    loading,
    profileLoading,
    isAdmin,
    isSuperAdmin,
    isTournamentAdmin,
    hasAccessToTournament,
    hasPermission,
    getTournamentAdminList,
    isAdminClaim,
    isAdminUser,
    tournamentAdminList,
    grantedPermissions,
    moderationStatus,
    isCommentBanned,
    isSiteBanned,
    muteActiveUntil,
  };

  return (
    <AuthContext.Provider value={value}>{!loading ? children : <div></div>}</AuthContext.Provider>
  );
};
